/**
 * Dashboard del operador del stack (Pagafácil S.L.).
 *
 * Acceso: magic link al ADMIN_EMAIL.
 *  - /operador            → form para pedir magic link
 *  - /operador?t=<token>  → dashboard agregado
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import TopBar from '../components/TopBar';


function fmtEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtInt(n: number): string {
  return n.toLocaleString('es-ES');
}


export default function OperatorPage() {
  const [params] = useSearchParams();
  const token = params.get('t');

  if (!token) return <RequestAccessView />;
  return <DashboardView token={token} />;
}


function RequestAccessView() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/operator/lookup', { email });
      setSent(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Operador" />
      <header className="bg-paper border-b border-line">
        <div className="max-w-md mx-auto px-6 py-7">
          <h1 className="font-display font-bold text-[32px] text-ink tracking-[-0.02em]">
            Operador del stack
          </h1>
          <p className="text-[13.5px] text-ink-500 mt-1">
            Solo para el operador de Pagafácil S.L.
          </p>
        </div>
      </header>
      <main className="max-w-md mx-auto px-6 py-10">
        {sent ? (
          <div className="bg-olive-50 border border-[#C7D5A8] rounded-card p-5 text-[14px] text-olive-700">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] mb-2">
              · Mira tu email
            </div>
            Si ese email es el del operador, recibirás un magic link en 1-2 min.
            El enlace caduca en 60 minutos.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-[13.5px] text-ink-700 mb-3">
              Introduce el email del operador del stack. Si coincide, recibirás un magic link.
            </p>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="hola@pagafacil.eu"
              className="cc-input"
            />
            <button
              type="submit"
              disabled={sending}
              className="w-full px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
            >
              {sending ? 'Un momento…' : 'Mandarme el enlace'}
            </button>
          </form>
        )}
      </main>
      <Style />
    </div>
  );
}


function DashboardView({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/operator/dashboard?t=${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'Error cargando'));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-8 text-center font-sans">
        <div>
          <h1 className="font-display font-bold text-[24px] text-ink mb-2">{error}</h1>
          <Link to="/operador" className="text-brand hover:text-brand-700 underline">
            Pedir otro enlace →
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center font-sans text-ink-500">
        Un momento…
      </div>
    );
  }

  const t = data.totals;
  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Operador del stack" />
      <header className="bg-paper border-b border-line">
        <div className="max-w-6xl mx-auto px-6 py-7 flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-bold text-[28px] md:text-[36px] text-ink tracking-[-0.02em]">
              Dashboard del stack
            </h1>
            <p className="text-[12.5px] text-ink-500 font-mono">
              · generado {new Date(data.generated_at).toLocaleString('es-ES')}
            </p>
          </div>
          {/* Acciones rápidas — feedback Ana 31-jul: "no me da opción de crear
              una nueva web". Las rutas /entidad/nueva y /entidades ya existían
              pero no había CTA visible desde el panel maestro. */}
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/entidad/nueva"
              target="_blank"
              className="px-4 py-2 rounded-lg font-mono text-[12px] uppercase tracking-wider bg-brand text-brand-ink font-bold hover:opacity-90 no-underline"
            >
              + Nueva organización
            </Link>
            <Link
              to="/entidades"
              target="_blank"
              className="px-4 py-2 rounded-lg font-mono text-[12px] uppercase tracking-wider bg-white border border-line text-ink hover:bg-line/30 no-underline"
            >
              Buscar organización
            </Link>
            <Link
              to={`/operador/eventos?t=${encodeURIComponent(token)}`}
              target="_blank"
              className="px-4 py-2 rounded-lg font-mono text-[12px] uppercase tracking-wider bg-white border border-line text-ink hover:bg-line/30 no-underline"
            >
              Editar eventos
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* KPIs */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Negocio
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="GMV total" value={fmtEUR(t.gmv_total_eur)} accent="olive" />
            <Kpi label="GMV este mes" value={fmtEUR(t.gmv_month_eur)} accent="brand" />
            <Kpi label="Comisión total estimada" value={fmtEUR(t.commission_total_eur_estimated)} accent="terra" />
            <Kpi label="Comisión este mes" value={fmtEUR(t.commission_month_eur_estimated)} accent="terra" />
          </div>
          <p className="text-[11px] text-ink-500 mt-2 font-mono">
            · {data.notes.commission_disclaimer}
          </p>
        </section>

        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Volumen
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Entidades" value={fmtInt(t.entities)} accent="ink" />
            <Kpi label="Cobros" value={fmtInt(t.group_buys)} accent="ink" />
            <Kpi label="Órdenes" value={fmtInt(t.ticket_orders)} accent="ink" />
            <Kpi label="Órdenes pagadas" value={fmtInt(t.ticket_orders_paid)} accent="olive" />
          </div>
        </section>

        {/* Por vertical */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Por vertical
          </div>
          <DistributionTable data={data.by_vertical} />
        </section>

        {/* Por status */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Por estado
          </div>
          <DistributionTable data={data.by_status} />
        </section>

        {/* Por pasarela */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Por pasarela
          </div>
          <DistributionTable data={data.by_provider} />
        </section>

        {/* Entidades por tipo */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Entidades por tipo
          </div>
          <div className="bg-paper border border-line rounded-card p-5">
            <ul className="divide-y divide-line-soft">
              {Object.entries(data.entities_by_type).map(([key, count]) => (
                <li key={key} className="py-2 flex items-baseline justify-between text-[13.5px]">
                  <span className="text-ink-700">{key.replace('_', ' ')}</span>
                  <span className="font-display font-bold text-ink tabular-nums">{fmtInt(count as number)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Top entidades por GMV */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Top entidades por GMV
          </div>
          <div className="bg-paper border border-line rounded-card p-5">
            {data.top_entities.length === 0 ? (
              <p className="text-[13px] text-ink-500">Sin entidades aún.</p>
            ) : (
              <ul className="divide-y divide-line-soft">
                {data.top_entities.map((e: any, i: number) => (
                  <li key={e.slug} className="py-2.5 flex items-baseline gap-3 text-[13.5px]">
                    <span className="font-mono text-[11px] text-ink-500 w-6 shrink-0">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-ink truncate">{e.name}</div>
                      <div className="text-[11.5px] text-ink-500 truncate">
                        {e.entity_type.replace('_', ' ')} · {e.cobros} cobro{e.cobros === 1 ? '' : 's'}
                      </div>
                    </div>
                    <span className="font-display font-bold text-olive-700 tabular-nums whitespace-nowrap">
                      {fmtEUR(e.gmv_eur)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Recurrencias */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Facturas recurrentes (Oficina Fácil)
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Activas" value={fmtInt(data.recurring.active)} accent="brand" />
            <Kpi label="Próximas 7 días" value={fmtInt(data.recurring.due_in_7_days)} accent="terra" />
            <Kpi label="Total facturado" value={fmtEUR(data.recurring.total_billed_eur)} accent="olive" />
          </div>
        </section>

        {/* Health */}
        <section>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Salud del sistema
          </div>
          <div className="bg-paper border border-line rounded-card p-5">
            <div className="flex items-baseline justify-between text-[13.5px] mb-2">
              <span className="text-ink-700">Tamaño BD</span>
              <span className="font-display font-bold tabular-nums">
                {data.health.db_size_mb.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB
                {data.health.db_size_mb > 1024 && (
                  <span className="text-ink-500 font-mono text-[11px] ml-2">
                    ({(data.health.db_size_mb / 1024).toFixed(2)} GB)
                  </span>
                )}
              </span>
            </div>
            <div className="text-[11.5px] text-ink-500">
              {data.health.blob_threshold_reached ? (
                <span className="text-brick">⚠ Supera el umbral de {data.health.blob_threshold_gb} GB · revisar migración de BLOBs a R2 (ADR-004).</span>
              ) : (
                <span>Por debajo del umbral de {data.health.blob_threshold_gb} GB (migración a R2 aún no necesaria · ADR-004).</span>
              )}
            </div>
          </div>
        </section>

        <p className="text-center text-[11px] text-ink-500 font-mono pt-8">
          · Pagafácil S.L. · este panel no reemplaza a la contabilidad oficial.
        </p>
      </main>
      <Style />
    </div>
  );
}


function DistributionTable({ data }: { data: Record<string, { count: number; gmv_eur: number }> }) {
  const entries = Object.entries(data).sort((a, b) => b[1].gmv_eur - a[1].gmv_eur);
  const totalGmv = entries.reduce((s, [, v]) => s + v.gmv_eur, 0);
  if (entries.length === 0) return (
    <div className="bg-paper border border-line rounded-card p-5 text-[13px] text-ink-500">
      Sin datos.
    </div>
  );
  return (
    <div className="bg-paper border border-line rounded-card p-5">
      <ul className="space-y-2.5">
        {entries.map(([key, v]) => {
          const pct = totalGmv > 0 ? Math.round((v.gmv_eur / totalGmv) * 100) : 0;
          return (
            <li key={key}>
              <div className="flex items-baseline justify-between text-[13px] mb-1">
                <span className="text-ink truncate">{key.replace('_', ' ')}</span>
                <span className="font-mono text-[11.5px] text-ink-500 whitespace-nowrap">
                  {fmtInt(v.count)} · {fmtEUR(v.gmv_eur)} · {pct}%
                </span>
              </div>
              <div className="h-1.5 bg-line rounded-pill overflow-hidden">
                <div className="h-full bg-olive rounded-pill" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function Kpi({ label, value, accent }: { label: string; value: string; accent: 'olive' | 'brand' | 'terra' | 'ink' }) {
  const color = accent === 'olive' ? 'text-olive-700'
              : accent === 'brand' ? 'text-brand-700'
              : accent === 'terra' ? 'text-terra-700'
              : 'text-ink';
  return (
    <div className="bg-paper border border-line rounded-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
        · {label}
      </div>
      <div className={`font-display font-bold text-[18px] tabular-nums tracking-[-0.01em] ${color}`}>
        {value}
      </div>
    </div>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:11px 14px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
    `}</style>
  );
}
